import {
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { superAdminApi } from '../api/superAdminApi';

interface UnitPickerDialogProps {
  open: boolean;
  onClose: () => void;
  /** Cilovy pathname ktery otevreme s ?unitId=X (default /admin). */
  targetPath?: string;
}

/**
 * SUPER_ADMIN picker: zobrazi seznam jednotek a po vyberu naviguje
 * na `{targetPath}?unitId={id}` (impersonation SA -> unit admin kontext).
 */
export const UnitPickerDialog = ({ open, onClose, targetPath = '/admin' }: UnitPickerDialogProps) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');

  const { data: units = [], isLoading } = useQuery({
    queryKey: ['superadmin', 'units'],
    queryFn: superAdminApi.listUnits,
    enabled: open,
    staleTime: 60_000,
  });

  const active = useMemo(
    () =>
      units
        .filter((u) => u.isActive)
        .filter((u) => {
          if (!filter.trim()) return true;
          const q = filter.trim().toLowerCase();
          return u.name.toLowerCase().includes(q) || u.slug.toLowerCase().includes(q);
        })
        .sort((a, b) => a.name.localeCompare(b.name, 'cs')),
    [units, filter],
  );

  const inactive = units.filter((u) => !u.isActive);

  const pick = (unitId: number) => {
    const sep = targetPath.includes('?') ? '&' : '?';
    navigate(`${targetPath}${sep}unitId=${unitId}`);
    onClose();
    setFilter('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <SupervisorAccountIcon color="primary" />
          <span>Přejít do administrace jednotky</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Vyberte jednotku, do jejíž administrace chcete vstoupit. Aktuálně zobrazený obsah
          (soutěže, uživatelé, šablony) bude filtrovaný na tuto jednotku.
        </Typography>
        <TextField
          autoFocus
          size="small"
          fullWidth
          placeholder="Filtrovat podle názvu…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ mb: 1 }}
        />
        {isLoading ? (
          <Stack alignItems="center" py={3}>
            <CircularProgress size={24} />
          </Stack>
        ) : active.length === 0 ? (
          <Typography color="text.secondary" variant="body2" sx={{ py: 2, textAlign: 'center' }}>
            {filter ? 'Žádná jednotka neodpovídá filtru.' : 'Žádné aktivní jednotky.'}
          </Typography>
        ) : (
          <List disablePadding>
            {active.map((u) => (
              <ListItem key={u.id} disablePadding>
                <ListItemButton onClick={() => pick(u.id)}>
                  <ListItemText
                    primary={u.name}
                    secondary={`/${u.slug}`}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                  <Chip label="Aktivní" size="small" color="success" variant="outlined" />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
        {inactive.length > 0 && !filter && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            {inactive.length} deaktivovaných jednotek není v seznamu.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};
